/**
 * Вход в кабинет.
 *
 * Сессия ставится сервером в httpOnly-cookie, поэтому в браузере после успешного
 * входа не остаётся ничего, что можно было бы украсть скриптом (§8.1).
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate } from '@tanstack/react-router';

import type { SessionUser } from '@cc/shared';

import { ApiError, login } from '../shared/api/client.js';
import { SESSION_QUERY_KEY, useSession } from '../app/auth-guard.js';
import { Button } from '../shared/ui/button.js';
import { Field } from '../shared/ui/field.js';

/** Куда попадает пользователь сразу после входа: диспетчер работает, остальные управляют */
function landingFor(role: SessionUser['role']): '/desk' | '/admin/queue' {
  return role === 'dispatcher' ? '/desk' : '/admin/queue';
}

export function LoginPage(): React.JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useSession();
  const [form, setForm] = useState({ login: '', password: '' });

  const mutation = useMutation({
    mutationFn: login,
    onSuccess: async (user) => {
      queryClient.setQueryData(SESSION_QUERY_KEY, user);
      await navigate({ to: landingFor(user.role) });
    },
  });

  const error =
    mutation.error instanceof ApiError
      ? mutation.error.status === 401
        ? 'Неверный логин или пароль'
        : mutation.error.message
      : mutation.error
        ? 'Сервер не отвечает, попробуйте ещё раз'
        : undefined;

  // Проверка стоит после всех хуков: ранний выход выше по коду менял бы их
  // количество между отрисовками, и React падает с «Rendered fewer hooks».
  // Вошедшему форма не нужна — иначе после кнопки «назад» он видит поля логина
  // поверх работающего кабинета и решает, что его разлогинило
  if (!session.isLoading && session.user) {
    return <Navigate to={landingFor(session.user.role)} replace />;
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <form
        className="flex w-full max-w-sm flex-col gap-5 rounded-lg border border-slate-200 bg-white p-6"
        onSubmit={(event) => {
          event.preventDefault();
          mutation.mutate(form);
        }}
      >
        <h1 className="text-xl font-medium text-slate-900">Кабинет колл-центра</h1>

        <Field
          label="Логин"
          autoComplete="username"
          value={form.login}
          onChange={(event) => { setForm((f) => ({ ...f, login: event.target.value })); }}
        />
        <Field
          label="Пароль"
          type="password"
          autoComplete="current-password"
          value={form.password}
          onChange={(event) => { setForm((f) => ({ ...f, password: event.target.value })); }}
          {...(error !== undefined ? { error } : {})}
        />

        <Button type="submit" variant="primary" size="lg" busy={mutation.isPending}>
          Войти
        </Button>
      </form>
    </main>
  );
}
